import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Los tipos que se pueden encender y apagar.
 *
 * TIENE QUE SER LA LISTA ENTERA (02-09-2026). Faltaban cuatro —tarea
 * modificada, empezada, pausada y el parte de campo—, así que llegaban avisos
 * que nadie podía silenciar: el disparador de la base de datos consulta esta
 * tabla por tipo, y un tipo sin fila no se apaga nunca. Y sobraban dos que ya no
 * existen. `notificationTypes.parity.test.ts` compara esta lista con los tipos
 * que la aplicación sabe pintar y con los textos de los nueve idiomas.
 */
export const NOTIFICATION_TYPES = [
  'task_created',
  'task_assigned',
  'task_updated',
  'task_started',
  'task_stopped',
  'task_due_soon',
  'followup_not_started',
  'followup_unfinished',
  'task_completed',
  'task_cancelled',
  'worker_quick_report',
  'treatment_logged',
  'low_stock',
  'product_request',
  // Auditoría del perito (26-09-2026): la vuelta de una solicitud (M7) y el foco
  // nuevo grave (M17).
  'product_request_resolved',
  'pest_focus_new',
  'pest_focus_worse',
  'team_member_added',
  'irm_warning',
] as const;

export type NotificationType = typeof NOTIFICATION_TYPES[number];

export interface NotificationPreferenceRow {
  id: string;
  user_id: string;
  company_id: string;
  notification_type: string;
  enabled: boolean;
}

export function useNotificationPreferences() {
  const { user, company } = useAuth();
  const queryClient = useQueryClient();

  const { data: preferences = [], isLoading } = useQuery({
    queryKey: ['notification-preferences', user?.id, company?.id],
    queryFn: async () => {
      if (!user?.id || !company?.id) return [];
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .eq('company_id', company.id);
      if (error) throw error;
      return (data ?? []) as NotificationPreferenceRow[];
    },
    enabled: !!user?.id && !!company?.id,
  });

  const prefMap = new Map(preferences.map(p => [p.notification_type, p.enabled]));

  const isTypeEnabled = useCallback(
    (type: string): boolean => {
      const val = prefMap.get(type);
      return val === undefined ? true : val;
    },
    [prefMap]
  );

  const disabledTypes = new Set(
    NOTIFICATION_TYPES.filter(t => !isTypeEnabled(t))
  );

  const toggleType = useMutation({
    mutationFn: async ({ type, enabled }: { type: string; enabled: boolean }) => {
      if (!user?.id || !company?.id) return;
      const existing = preferences.find(p => p.notification_type === type);
      if (existing) {
        const { error } = await supabase
          .from('notification_preferences')
          .update({ enabled })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('notification_preferences')
          .insert({
            user_id: user.id,
            company_id: company.id,
            notification_type: type,
            enabled,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });

  return {
    isTypeEnabled,
    disabledTypes,
    toggleType: toggleType.mutate,
    isToggling: toggleType.isPending,
    isLoading,
  };
}
